import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import ContactUsModal from '@ep/components/Layout/Header/ContactUs/ContactUsModal';
import Link from '@ep/components/Link';
import Media from '@ep/components/Media';
import SeeMoreLink from '@ep/components/SeeMoreLink';
import { H1, Label, Subhead2 } from '@ep/components/Typography';
import VideoButton from '@ep/components/VideoButton';
import theme from '@ep/theme';
import { Gcms_Asset, Gcms_RichText } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import { buttonBaseClasses } from '@mui/material';
import React from 'react';

export interface HeroCTAModuleProps {
  label?: string;
  heading: string;
  subheading: Gcms_RichText;
  buttonLabel?: string;
  buttonLink?: string;
  unstyledButtonLabel?: string;
  unstyledButtonLink?: string;
  background: Gcms_Asset;
  icon: Gcms_Asset;
  labelColor?: string;
  right?: boolean;
  imageOnly?: boolean;
  pageHasSectionMenu?:boolean;
}

interface StyledContainerProps {
  right?: boolean;
  pageHasSectionMenu?: boolean;
}

const StyledContainer = styled(Container)<StyledContainerProps>`
  display: flex;
  justify-content: ${({ right }) => (right ? 'flex-end' : 'flex-start')};
  padding: 5rem 0 5rem;

  ${theme.mediaQueries.tablet} {
    ${({pageHasSectionMenu})=>{ 
      return pageHasSectionMenu ? 'padding: 8rem 0 9rem;' : 'padding: 5rem 0 9rem;';
    }}
  }

  ${theme.mediaQueries.mobile} {
    ${({pageHasSectionMenu})=>{ 
      return pageHasSectionMenu ? 'padding: 8rem 0 3.75rem;' : 'padding: 5rem 0 3.75rem;';
    }}
  }
`;

const StyledMedia = styled(Media)`
  width: 3rem;
  float: right;

  ${theme.mediaQueries.mobile} {
    width: 2rem;
  }
`;

const Box = styled.div`
  border-radius: ${theme.radius.small};
  background-color: ${theme.colors.white85};
  padding: 2rem 2rem 2rem;
  box-shadow: ${theme.shadows[2]};

  ${theme.mediaQueries.tabletAndDesktop} {
    width: 28rem;
  }

  ${theme.mediaQueries.mobile} {
    padding: 2.5rem 1.5rem;
  }
`;

const StyledButton = styled(Button)`
    ${theme.mediaQueries.mobile} {
       height: 3rem;
    }
`;

const StyledLabel = styled(Label)`
  margin-bottom: 0.5rem;
  ${({ color }) => (color ? `color: ${color};` : '')}
`;

const Subheading = styled(Subhead2)`
  margin: 2rem 0;
  a {
    color: ${theme.colors.oceanBlue};
  }
`;

export const HeroLabel = styled.h1`
  font-family: ${theme.typography.boldFont};
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
`;

export const HeroCatch = styled.h3`
  font-family: ${theme.typography.boldFont};
  font-size: 3.2rem;
  margin: 0.5rem 1 0;
  line-height: 1.1;
  color: ${theme.colors.duskBlue};
  white-space: pre-wrap;
  ${theme.mediaQueries.mobileAndTablet} {
    font-size: 2.5rem;
  }
`;

const StyledLinkContainer = styled ('div')`
  margin-top: 1rem;
`;

function HeroCTAModule({
  icon,
  label,
  heading,
  subheading,
  buttonLabel,
  buttonLink,
  unstyledButtonLabel,
  unstyledButtonLink,
  background,
  labelColor,
  right,
  imageOnly,
  pageHasSectionMenu,
}: HeroCTAModuleProps) {
  if (imageOnly) {
    return (
      <StyledContainer background={background}>
        <div style={{ height: '16rem' }} />
      </StyledContainer>
    );
  }

  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    const contactForm = document.getElementById('contact-form');

    if (contactForm) {
      contactForm.scrollIntoView({ block: 'center' });
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // const handleLinkClick = (linkLabel: string) => {
  //   dataLayer.push({
  //     event: 'Page Interaction – user clicked on hero link - ' + linkLabel,
  //   });
  // };

  return (
    <StyledContainer background={background} right={right} pageHasSectionMenu={pageHasSectionMenu}>
      <Box>
        {label && <HeroLabel color={labelColor}>{label}</HeroLabel>}
        <HeroCatch>{heading}</HeroCatch>
        {subheading && <Subheading {...getHtmlProp(subheading.html)} />}
        {buttonLabel &&
          (buttonLink === '#contact-us' ? (
            <>
              <StyledButton
                size="large"
                label={buttonLabel}
                onClick={() => handleOpen()}
              />
              <ContactUsModal open={open} onClose={() => handleClose()} />
            </>
          ) : 
          (
            buttonLink?.startsWith('https://play.vidyard.com/') ? (
            <VideoButton label={buttonLabel} link={buttonLink} />
            ) : (
            <Button size="large" label={buttonLabel} link={buttonLink ? buttonLink : '/'} />
            )
          )
          )
          }
          {unstyledButtonLabel && (
            <StyledLinkContainer>
              <SeeMoreLink
                to={unstyledButtonLink ? unstyledButtonLink : '/'}
                label={unstyledButtonLabel}
                style={{ marginTop: '1rem' }}>
              </SeeMoreLink>
            </StyledLinkContainer>
          )}
      </Box>
    </StyledContainer>
  );
}

export default React.memo(HeroCTAModule);
